'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'

/** The two legal documents we publish (`/privacy`, `/terms`). */
export type LegalDocKey = 'privacy' | 'terms'

/** A paragraph (or list item) that opens with a bolded lead-in label. */
interface LabelledText {
  label: string
  /** Continuation after the label. Absent when the label is the whole line. */
  text?: string
}

type LegalListItem = string | LabelledText

/**
 * One renderable unit inside a section. The shapes exist so the JSON can carry
 * the source document's structure — running paragraphs, bulleted lists, and the
 * address block whose lines are separated by `<br />` rather than paragraphs.
 */
type LegalBlock = string | LabelledText | { list: LegalListItem[] } | { lines: string[] }

interface LegalSection {
  heading: string
  body: LegalBlock[]
}

interface LegalDocContent {
  title: string
  /** Present on the privacy policy (the operating-entity statement); terms has none. */
  intro?: string
  sections: LegalSection[]
}

function isListBlock(block: LegalBlock): block is { list: LegalListItem[] } {
  return typeof block === 'object' && 'list' in block
}

function isLinesBlock(block: LegalBlock): block is { lines: string[] } {
  return typeof block === 'object' && 'lines' in block
}

function LabelledLine({ item }: { item: LabelledText }) {
  return (
    <>
      <strong>{item.label}</strong>
      {item.text}
    </>
  )
}

function BlockView({ block }: { block: LegalBlock }) {
  if (typeof block === 'string') return <p>{block}</p>
  if (isListBlock(block)) {
    return (
      <ul>
        {block.list.map((item, index) => (
          <li key={typeof item === 'string' ? item : item.label + index}>
            {typeof item === 'string' ? item : <LabelledLine item={item} />}
          </li>
        ))}
      </ul>
    )
  }
  if (isLinesBlock(block)) {
    return (
      <p>
        {block.lines.map((line, index) => (
          <React.Fragment key={line}>
            {index > 0 && <br />}
            {line}
          </React.Fragment>
        ))}
      </p>
    )
  }
  return (
    <p>
      <LabelledLine item={block} />
    </p>
  )
}

/**
 * Renders one legal document (`/privacy` or `/terms`) from i18n.
 *
 * The copy is a reviewed legal text, so it lives entirely in
 * `packages/core/src/i18n/locales/{zh-TW,en}/web.json` under `legal.<doc>` and is
 * rendered structurally here — amending a clause is a JSON edit, never a JSX one.
 * `zh-TW` is the authoritative version; the English rendering carries
 * `legal.translationNotice` at the top saying so (shown for every non-Chinese
 * locale, never for zh).
 *
 * Section headings are real `<h2>`s, so they render in `--font-display` (the
 * self-hosted Noto Serif TC subset). Their glyphs come from an i18n ARRAY the
 * `<h1>/<h2>` source scan can't see, so both `legal.<doc>.title` and
 * `legal.<doc>.sections` are declared in `scripts/lib/collect-display-charset.mjs`
 * — after editing any heading, run `pnpm --filter @kiao-voice/web font:subset`.
 */
export function LegalDocument({ doc }: { doc: LegalDocKey }) {
  const { t, i18n } = useTranslation('web')
  const content = t(`legal.${doc}`, { returnObjects: true }) as unknown as LegalDocContent
  // A missing/renamed key makes i18next hand back the key string. Fail loudly:
  // a legal page that silently renders empty would still pass App Store review's
  // "the URL loads" check while publishing no policy at all.
  if (!content || typeof content !== 'object' || !Array.isArray(content.sections)) {
    throw new Error(`Missing i18n content for web:legal.${doc}`)
  }
  const showTranslationNotice = !(i18n.language ?? '').startsWith('zh')

  return (
    <article className="legal">
      <h1>{content.title}</h1>
      {showTranslationNotice && <p className="legal-notice">{t('legal.translationNotice')}</p>}
      {content.intro && <p className="legal-intro">{content.intro}</p>}
      {content.sections.map((section) => (
        <section className="legal-section" key={section.heading}>
          <h2>{section.heading}</h2>
          {section.body.map((block, index) => (
            <BlockView key={index} block={block} />
          ))}
        </section>
      ))}
    </article>
  )
}
